import type { TFunction } from 'react-i18next';
import { formatPhoneNumberIntl, parsePhoneNumber } from 'react-phone-number-input';

import { ELoginType } from '@/config/auth';

import type { TAddress, TOption } from '@/types/common';

export const capitalize = (str = '') =>
  `${str.substr(0, 1).toUpperCase()}${str.substr(1).toLowerCase()}`;

/**
 * Groups a national number the way the phone field shows an Indian number: "626 266 2662".
 * A leading 0 is the trunk prefix, so it is left off. A 10-digit number uses groups of 3, 3
 * and 4. A shorter number uses groups of 3 while it is being typed.
 */
export const groupPhoneDigits = (digits: string) => {
  const national = digits.replace(/\D/g, '').replace(/^0/, '');

  if (!national) {
    return '';
  }

  if (national.length === 10) {
    return `${national.slice(0, 3)} ${national.slice(3, 6)} ${national.slice(6)}`;
  }

  const parts: string[] = [];
  let rest = national;

  while (rest.length > 3) {
    parts.push(rest.slice(0, 3));
    rest = rest.slice(3);
  }

  if (rest) {
    parts.push(rest);
  }

  return parts.join(' ');
};

export const formatPhone = (phone: string) => {
  const parsed = parsePhoneNumber(phone);

  if (parsed?.country === 'US') {
    return `+${parsed.countryCallingCode} ${parsed.formatNational()}`;
  }

  return formatPhoneNumberIntl(phone);
};

export const formatTimeLeft = (coolDown: number) => {
  const minutes = `${Math.floor(coolDown / 60)}`.padStart(2, '0');
  const seconds = `${Math.floor(coolDown % 60)}`.padStart(2, '0');

  return `${minutes}:${seconds}`;
};

export const formatIdType = (type: ELoginType, t: TFunction): string => {
  switch (type) {
    case ELoginType.PHONE:
      return t('common.phone');

    case ELoginType.EMAIL:
      return t('common.email');

    // no default
  }
};

export const formatIdValue = (type: ELoginType, value: string): string => {
  switch (type) {
    case ELoginType.PHONE:
      return formatPhone(value);

    default:
      return value;
  }
};

export const highlightMatch = (value: string, query: string) => {
  if (!query) {
    return value;
  }

  const regex = new RegExp(query.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
  return value.replace(regex, (str) => `<strong>${str}</strong>`);
};

export const formatAddress = ({ addressLine1, city, state, zipCode }: TAddress) =>
  [addressLine1, city, [state, zipCode].filter(Boolean).join(' ')].filter(Boolean).join(', ');

export const optionToDisplay = <PValue extends string>(
  options: TOption<PValue>[],
  value: PValue | undefined,
  t: TFunction,
) => {
  const option = options.find((opt) => opt.value === value);

  return option ? t(option.label) : '';
};

export const toCdn = (path: string | null | undefined) => {
  if (!path) {
    return '';
  }

  if (/https?:\/\//.test(path)) {
    return path;
  }

  return `${window.env.CDN_HOST}/${path}`;
};

export const toFullName = ({ firstName, lastName }: { firstName?: string; lastName?: string }) =>
  [firstName, lastName].filter(Boolean).join(' ');

export const summarize = (explicit: string[], all: unknown[], t: TFunction, moreKey: string) => {
  const rest = all.length - explicit.length;
  const base = explicit.join(', ');

  if (rest > 0) {
    return `${base}... ${t(moreKey, { value: rest })}`;
  }

  return base;
};
